import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft, FiChevronDown, FiChevronUp, FiHelpCircle, FiShoppingBag, FiTruck, FiCreditCard, FiShield, FiUser } from 'react-icons/fi';

const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className="border-b border-gray-200 last:border-0">
    <button
      onClick={onClick}
      className="w-full py-4 px-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
    >
      <span className="font-medium text-gray-800 pr-4">{question}</span>
      {isOpen ? <FiChevronUp className="text-[#DC143C] flex-shrink-0" /> : <FiChevronDown className="text-gray-400 flex-shrink-0" />}
    </button>
    {isOpen && (
      <div className="px-4 pb-4 text-gray-600 text-sm leading-relaxed animate-fadeIn">
        {answer}
      </div>
    )}
  </div>
);

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [activeCategory, setActiveCategory] = useState('geral');

  const categories = [
    { id: 'geral', label: 'Geral', icon: FiHelpCircle },
    { id: 'compras', label: 'Compras', icon: FiShoppingBag },
    { id: 'entregas', label: 'Entregas', icon: FiTruck },
    { id: 'pagamentos', label: 'Pagamentos', icon: FiCreditCard },
    { id: 'seguranca', label: 'Segurança', icon: FiShield },
    { id: 'conta', label: 'Minha Conta', icon: FiUser },
  ];

  const faqs = {
    geral: [
      {
        question: 'O que é o BarueriFlash?',
        answer: 'O BarueriFlash é um marketplace de eletrônicos focado na cidade de Barueri, SP. Conectamos compradores a lojas locais com entrega rápida, garantindo praticidade e segurança nas suas compras.'
      },
      {
        question: 'O BarueriFlash atende outras cidades?',
        answer: 'Atualmente, nosso serviço está disponível exclusivamente para a cidade de Barueri, SP. Estamos trabalhando para expandir para outras regiões em breve.'
      },
      {
        question: 'Como faço para entrar em contato com o suporte?',
        answer: 'Você pode entrar em contato conosco pelo email suporte@barueriflash.com.br ou através do WhatsApp disponível no rodapé do site. Nosso horário de atendimento é de segunda a sábado, das 8h às 20h.'
      },
      {
        question: 'O BarueriFlash é seguro?',
        answer: 'Sim! Utilizamos criptografia de ponta a ponta, pagamentos seguros via PIX (Mercado Pago), e todos os nossos entregadores passam por verificação de documentos antes de serem aprovados.'
      }
    ],
    compras: [
      {
        question: 'Como faço uma compra?',
        answer: 'É simples: 1) Navegue pelos produtos e adicione ao carrinho; 2) Vá para o checkout; 3) Informe seu endereço em Barueri; 4) Escolha o pagamento via PIX; 5) Aguarde a confirmação e acompanhe sua entrega em tempo real!'
      },
      {
        question: 'Posso comprar de várias lojas no mesmo pedido?',
        answer: 'Sim! Você pode adicionar produtos de diferentes lojas ao seu carrinho. Cada loja será tratada como um pedido separado, com sua própria entrega.'
      },
      {
        question: 'Qual o valor mínimo para compra?',
        answer: 'Não há valor mínimo para compras. Porém, lembre-se que a taxa de entrega de R$15 será aplicada a cada pedido.'
      },
      {
        question: 'Como sei se o produto está disponível?',
        answer: 'Todos os produtos exibidos na vitrine estão disponíveis em estoque. Caso um produto esteja esgotado, ele aparecerá com a indicação "ESGOTADO".'
      }
    ],
    entregas: [
      {
        question: 'Quanto tempo leva a entrega?',
        answer: 'Nosso objetivo é entregar em até 2 horas após a confirmação do pagamento, dependendo da disponibilidade de entregadores e da distância até seu endereço.'
      },
      {
        question: 'Qual o valor da taxa de entrega?',
        answer: 'A taxa de entrega é de R$15,00 por pedido, independente da distância dentro de Barueri.'
      },
      {
        question: 'Como acompanho minha entrega?',
        answer: 'Após a confirmação do pagamento, você pode acompanhar sua entrega em tempo real pelo mapa na página "Meus Pedidos". Você receberá um código de segurança que deve ser informado ao entregador.'
      },
      {
        question: 'O que é o código de segurança?',
        answer: 'É um código de 4 dígitos gerado para cada pedido. Você deve informar esse código ao entregador no momento da entrega para confirmar o recebimento. Isso garante que o produto seja entregue à pessoa certa.'
      },
      {
        question: 'Posso agendar a entrega?',
        answer: 'No momento, não oferecemos agendamento de entregas. Todas as entregas são realizadas assim que um entregador aceita o pedido.'
      }
    ],
    pagamentos: [
      {
        question: 'Quais formas de pagamento são aceitas?',
        answer: 'Atualmente aceitamos apenas pagamento via PIX. Essa forma de pagamento garante confirmação instantânea e maior segurança para compradores e vendedores.'
      },
      {
        question: 'O pagamento é seguro?',
        answer: 'Sim! Utilizamos o Mercado Pago como intermediador de pagamentos, uma das maiores plataformas de pagamentos do Brasil. O pagamento vai diretamente para a conta do lojista, com toda a segurança.'
      },
      {
        question: 'Como funciona o pagamento PIX?',
        answer: 'Após finalizar o pedido, você receberá um QR Code ou código PIX para copiar e colar. Basta abrir o app do seu banco, escanear ou colar o código e confirmar. O pagamento é confirmado em segundos!'
      },
      {
        question: 'Posso cancelar após pagar?',
        answer: 'Cancelamentos só são possíveis antes do pedido ser preparado pela loja. Após o início da preparação, não é possível cancelar. Em caso de problemas, entre em contato com o suporte.'
      }
    ],
    seguranca: [
      {
        question: 'Como meus dados são protegidos?',
        answer: 'Utilizamos criptografia SSL/TLS em todas as comunicações, armazenamos senhas de forma segura com hash, e seguimos as melhores práticas de segurança da informação conforme a LGPD.'
      },
      {
        question: 'O BarueriFlash compartilha meus dados?',
        answer: 'Não vendemos ou compartilhamos seus dados pessoais com terceiros para fins de marketing. Seus dados são usados apenas para processar pedidos e melhorar sua experiência. Consulte nossa Política de Privacidade para mais detalhes.'
      },
      {
        question: 'Como os entregadores são verificados?',
        answer: 'Todos os entregadores passam por um processo de verificação onde devem enviar foto da CNH, placa do veículo e documento do veículo. Nossa equipe analisa e aprova manualmente cada cadastro.'
      },
      {
        question: 'O que fazer se tiver problemas com um pedido?',
        answer: 'Entre em contato imediatamente com nosso suporte pelo email ou WhatsApp. Temos um time dedicado para resolver qualquer problema o mais rápido possível.'
      }
    ],
    conta: [
      {
        question: 'Como criar uma conta?',
        answer: 'Clique em "Cadastre-se" na tela de login, escolha seu tipo de perfil (Comprador, Lojista ou Entregador), preencha seus dados e pronto! Você receberá acesso imediato.'
      },
      {
        question: 'Esqueci minha senha, o que faço?',
        answer: 'Na tela de login, clique em "Esqueceu sua senha?". Digite seu email cadastrado e você receberá um link para criar uma nova senha. O link expira em 1 hora.'
      },
      {
        question: 'Como alterar meus dados cadastrais?',
        answer: 'Acesse seu perfil no menu do aplicativo e clique em "Editar Perfil". Você pode alterar nome, telefone e endereço. Para alterar o email, entre em contato com o suporte.'
      },
      {
        question: 'Posso excluir minha conta?',
        answer: 'Sim. Para solicitar a exclusão da sua conta e dados pessoais, entre em contato com nosso suporte. Atenderemos sua solicitação em até 15 dias úteis, conforme a LGPD.'
      }
    ]
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#1A1A1A] to-[#DC143C] text-white">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-4">
            <FiArrowLeft /> Voltar
          </Link>
          <h1 className="text-2xl md:text-3xl font-bold">Perguntas Frequentes</h1>
          <p className="text-white/70 mt-2">Encontre respostas para suas dúvidas</p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Category Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-4 scrollbar-hide mb-6">
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => { setActiveCategory(cat.id); setOpenIndex(null); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all ${
                activeCategory === cat.id
                  ? 'bg-[#DC143C] text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              <cat.icon size={16} />
              <span className="text-sm font-medium">{cat.label}</span>
            </button>
          ))}
        </div>

        {/* FAQ List */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          {faqs[activeCategory]?.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={openIndex === index}
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
            />
          ))}
        </div>

        {/* Contact CTA */}
        <div className="mt-8 bg-gradient-to-r from-[#1A1A1A] to-[#2D2D2D] rounded-2xl p-6 text-white text-center">
          <h2 className="text-xl font-bold mb-2">Ainda tem dúvidas?</h2>
          <p className="text-white/70 mb-4">Nossa equipe está pronta para ajudar</p>
          <a
            href="mailto:suporte@barueriflash.com.br"
            className="inline-block px-6 py-3 bg-[#DC143C] rounded-xl font-semibold hover:bg-[#B01030] transition-colors"
          >
            Falar com Suporte
          </a>
        </div>

        {/* Legal Links */}
        <div className="mt-8 flex flex-wrap justify-center gap-4 text-sm text-gray-500">
          <Link to="/termos" className="hover:text-[#DC143C]">Termos de Uso</Link>
          <span>•</span>
          <Link to="/privacidade" className="hover:text-[#DC143C]">Política de Privacidade</Link>
          <span>•</span>
          <Link to="/sobre" className="hover:text-[#DC143C]">Sobre Nós</Link>
        </div>
      </main>
    </div>
  );
};

export default FAQ;
